import { useMemo, type ReactNode } from 'react';
import { styled } from 'goober';

import { Stack } from '@/ui/line/line';

import { projectIdState } from '../project.state';
import { projectListingSortState } from '../project-listing.state';

import { ProjectListingsTable } from './project-listings-table/project-listings-table';
import {
  resolveProjectListingsDisplayMode,
  type ProjectListingsDisplayMode,
} from './resolve-project-listings-display-mode';
import { StageManagementTable } from './stage-management-table/stage-management-table';
import { ProjectListingsHeader } from './project-listings-header';
import { ProjectListingsLoadingSkeleton } from './project-listings-loading-skeleton';
import { useChangeProjectListingsDisplayMode } from './use-change-project-listings-display-mode';
import { useProjectListings } from './use-project-listings.query';
import { useStageParam } from './stage-management-table/use-stage-param';
import { useProjectListingsTabParam } from './project-listings-table/use-project-listings-tab-param';
import { useDealStatuses } from './stage-management-table/use-deal-statuses';
import { ListingStageName } from '@/features/listing-stage/types';
import { useAtomValue } from 'jotai';

export type { ProjectListingsDisplayMode } from './resolve-project-listings-display-mode';

export const ProjectListings = ({
  isProjectLoading,
  isDisabledMode,
}: {
  isProjectLoading: boolean;
  isDisabledMode?: boolean;
}) => {
  const { displayMode, isStageMode, onDisplayModeChange } =
    useChangeProjectListingsDisplayMode();
  const listingsDisplayMode = resolveProjectListingsDisplayMode({
    displayMode,
    isDisabledMode,
  });
  const projectId = useAtomValue(projectIdState);
  const sort = useAtomValue(projectListingSortState);
  const dealStatuses = useDealStatuses();

  const getStageFilterParam = (stageId: string): string[] => {
    if (stageId === ListingStageName.deal) {
      return dealStatuses.map(status => status.id);
    } else {
      return [stageId];
    }
  };

  const { stageParam } = useStageParam();
  const { listingsTabParam } = useProjectListingsTabParam();

  const activeStageParam = useMemo(() => {
    if (isDisabledMode) {
      return undefined;
    }
    if (isStageMode) {
      return stageParam;
    }
    return listingsTabParam !== 'all' ? listingsTabParam : undefined;
  }, [isDisabledMode, isStageMode, listingsTabParam, stageParam]);

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isProjectListingsDataLoading,
    projectListings,
  } = useProjectListings({
    projectId,
    sort,
    ...(activeStageParam && {
      stageFilter: getStageFilterParam(activeStageParam),
    }),
    enabled: listingsTabParam !== 'pending-companies',
  });

  const companiesInProjectContentConfig: Record<
    ProjectListingsDisplayMode,
    ReactNode
  > = useMemo(() => {
    return {
      table: (
        <ProjectListingsTable
          hasNextPage={Boolean(hasNextPage)}
          isDisabledMode={isDisabledMode}
          isFetchingNextPage={Boolean(isFetchingNextPage)}
          isLoading={isProjectListingsDataLoading}
          listingsData={projectListings}
          onLoadMore={fetchNextPage}
        />
      ),
      byStage: (
        <StageManagementTable
          listingsData={projectListings}
          isLoading={isProjectListingsDataLoading}
        />
      ),
    };
  }, [
    fetchNextPage,
    hasNextPage,
    isDisabledMode,
    isFetchingNextPage,
    isProjectListingsDataLoading,
    projectListings,
  ]);

  return (
    <Wrapper data-testid="project-listings-component">
      {isProjectLoading ? (
        <ProjectListingsLoadingSkeleton />
      ) : (
        <>
          <ProjectListingsHeader
            displayMode={listingsDisplayMode}
            isDisabledMode={isDisabledMode}
            isLoading={isProjectListingsDataLoading}
            toggleDisplayMode={onDisplayModeChange}
          />

          {companiesInProjectContentConfig[listingsDisplayMode]}
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled(Stack)`
  margin-top: 45px;
`;
